import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import {
  Card,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

const PLACEHOLDER_IMG = "https://via.placeholder.com/500x500.png?text=Sem+Imagem";

function formatNum(v, dec = 2) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
}

function InfoItem({ label, value }) {
  if (value === null || value === undefined || value === "") return null;

  return (
    <MDBox>
      <MDTypography variant="caption" color="text" sx={{ fontSize: 11 }}>
        {label}
      </MDTypography>
      <MDTypography variant="button" display="block" sx={{ fontSize: 13 }}>
        {value}
      </MDTypography>
    </MDBox>
  );
}

export default function FichaProduto() {
  const { refmaterial } = useParams();
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const idUtilizador = Number(userData?.id || 0);

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const podeGerirStocks = temFuncao("Gestão Stocks");

  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState(null);

  const [pendentes, setPendentes] = useState(0);
  const [enviandoEmail, setEnviandoEmail] = useState(false);

  const [openStock, setOpenStock] = useState(false);
  const [stockSelecionado, setStockSelecionado] = useState(null);
  const [formStock, setFormStock] = useState({
    qtd_verificada: "",
    localizacao: "",
    obs: "",
  });

  const material = dados?.material || null;
  const stocks = Array.isArray(dados?.stocks) ? dados.stocks : [];
  const precos = dados?.precos || null;
  const imagemUrl = dados?.imagem?.url || PLACEHOLDER_IMG;
  const permissoes = dados?.permissoes || {};

  const podeAlterarStock = Boolean(permissoes.podeAlterarStock) || isAdminLike || podeGerirStocks;

  const carregarFicha = async () => {
    const ref = String(refmaterial || "").trim();

    if (!ref) {
      showAlert("Referência de material inválida.", "warning");
      return;
    }

    setLoading(true);

    const [res, err] = await handleApi(
      api.post("Stocks/SelFichaProduto", {
        refmaterial: ref,
        idutilizador: idUtilizador,
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      setDados(null);
      return;
    }

    setDados(res || null);
  };

  const carregarPendentes = async () => {
    if (!idUtilizador) return;

    const [res, err] = await handleApi(
      api.post("Stocks/SelStockAlteracoesPendentes", {
        idutilizador: idUtilizador,
      })
    );

    if (err) return;

    setPendentes(Number(res?.total || 0));
  };

  useEffect(() => {
    carregarFicha();
  }, [refmaterial, idUtilizador]);

  useEffect(() => {
    carregarPendentes();
  }, [idUtilizador]);

  const abrirAlterarStock = (stock) => {
    setStockSelecionado(stock);
    setFormStock({
      qtd_verificada: stock?.quantidade ?? "",
      localizacao: "",
      obs: "",
    });
    setOpenStock(true);
  };

  const fecharAlterarStock = () => {
    setOpenStock(false);
    setStockSelecionado(null);
    setFormStock({
      qtd_verificada: "",
      localizacao: "",
      obs: "",
    });
  };

  const gravarAlteracaoStock = async () => {
    if (!stockSelecionado || !material) return;

    const qtd = Number(String(formStock.qtd_verificada || "").replace(",", "."));

    if (!Number.isFinite(qtd) || qtd < 0) {
      showAlert("Indica uma quantidade válida.", "warning");
      return;
    }

    setLoading(true);

    const [res, err, meta] = await handleApi(
      api.post("Stocks/InsStockAlteracao", {
        refmaterial: material.refmaterial,
        refcentro: stockSelecionado.refcentro,
        qtd_verificada: qtd,
        localizacao: formStock.localizacao || "",
        obs: formStock.obs || "",
        idutilizador_registo: idUtilizador,
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(meta?.message || "Alteração de stock registada.", meta?.severity || "success");

    fecharAlterarStock();
    await carregarPendentes();
  };

  const enviarPendentes = async () => {
    if (!idUtilizador) return;

    setEnviandoEmail(true);

    const [res, err, meta] = await handleApi(
      api.post("Stocks/EnviarEmailStockAlteracoesPendentes", {
        idutilizador: idUtilizador,
      })
    );

    setEnviandoEmail(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(meta?.message || "Email enviado.", meta?.severity || "success");
    await carregarPendentes();
  };

  return (
    <MDBox
      sx={{
        minHeight: "100vh",
        background: "#f5f6fa",
        p: { xs: 1.5, md: 3 },
      }}
    >
      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      {pendentes > 0 && (
        <MDBox mb={2}>
          <MDBox
            p={1.5}
            borderRadius={2}
            sx={{ border: "1px solid #ffe0b2", background: "#fff8e1" }}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap"
          >
            <MDTypography variant="button" color="text">
              Existem <b>{pendentes}</b> alterações de stock pendentes de envio por e-mail.
            </MDTypography>

            <MDButton
              color="warning"
              size="small"
              onClick={enviarPendentes}
              disabled={enviandoEmail}
            >
              Enviar
            </MDButton>
          </MDBox>
        </MDBox>
      )}

      {!loading && !material && (
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h5">Material não encontrado</MDTypography>
          </MDBox>
        </Card>
      )}

      {material && (
        <>
          <Card>
            <MDBox
              p={{ xs: 2, md: 3 }}
              display="grid"
              gap={3}
              sx={{
                gridTemplateColumns: { xs: "1fr", md: "360px 1fr" },
              }}
            >
              <MDBox
                sx={{
                  background: "#fff",
                  border: "1px solid #eee",
                  borderRadius: 2,
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={imagemUrl}
                  alt={material.nome || material.refmaterial}
                  style={{
                    width: "100%",
                    maxHeight: 360,
                    objectFit: "contain",
                  }}
                />
              </MDBox>

              <MDBox>
                <MDTypography variant="h4" sx={{ lineHeight: 1.15 }}>
                  {material.nome}
                </MDTypography>

                <MDTypography variant="h6" color="text" mt={0.5}>
                  Ref.: {material.refmaterial}
                </MDTypography>

                <MDBox
                  mt={2}
                  display="grid"
                  gap={1.25}
                  sx={{
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
                  }}
                >
                  <InfoItem label="Fabricante" value={material.fabricante} />
                  <InfoItem label="Família" value={material.familia} />
                  <InfoItem label="Subfamília" value={material.subfamilia} />
                  <InfoItem label="Marca" value={material.marca} />
                  <InfoItem label="Submarca" value={material.submarca} />
                  <InfoItem label="Tipo" value={material.tipo} />
                  <InfoItem label="Segmento" value={material.segmento} />
                  <InfoItem label="Grupo" value={material.grupo} />
                  <InfoItem label="Capacidade" value={material.capacidade} />
                  <InfoItem label="Unidade" value={material.unidade} />
                  <InfoItem label="Volume" value={formatNum(material.volume, 3)} />
                  <InfoItem label="Peso líquido" value={formatNum(material.pesoliquido, 3)} />
                  <InfoItem label="Peso bruto" value={formatNum(material.pesobruto, 3)} />
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>

          <MDBox mt={2}>
            <Card>
              <MDBox p={{ xs: 2, md: 3 }}>
                <MDTypography variant="h6" mb={1.5}>
                  Preços
                </MDTypography>

                <MDBox
                  display="grid"
                  gap={1.5}
                  sx={{
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr 1fr" },
                  }}
                >
                  <InfoItem
                    label="PVP recomendado"
                    value={
                      precos?.zpr0 !== null && precos?.zpr0 !== undefined
                        ? `${formatNum(precos.zpr0)} €`
                        : ""
                    }
                  />

                  {isAdminLike && (
                    <>
                      <InfoItem
                        label="Preço de custo"
                        value={
                          precos?.zpvr !== null && precos?.zpvr !== undefined
                            ? `${formatNum(precos.zpvr)} €`
                            : ""
                        }
                      />

                      <InfoItem
                        label="Margem"
                        value={
                          precos?.zmrg !== null && precos?.zmrg !== undefined
                            ? `${formatNum(precos.zmrg)}`
                            : ""
                        }
                      />

                      <InfoItem
                        label="Desconto máximo autorizado"
                        value={
                          material.descontopromocional !== null &&
                          material.descontopromocional !== undefined
                            ? `${formatNum(material.descontopromocional, 2)} %`
                            : "Sem valor definido"
                        }
                      />
                    </>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>

          <MDBox mt={2}>
            <Card>
              <MDBox p={{ xs: 2, md: 3 }}>
                <MDTypography variant="h6" mb={1.5}>
                  Stocks por centro
                </MDTypography>

                <MDBox sx={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      minWidth: 640,
                      borderCollapse: "collapse",
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={thStyle}>Centro</th>
                        <th style={thStyle}>Ref. centro</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>Quantidade</th>
                        <th style={thStyle}>Unidade</th>
                        {podeAlterarStock && <th style={thStyle}>Ação</th>}
                      </tr>
                    </thead>

                    <tbody>
                      {stocks.map((s, idx) => (
                        <tr key={`${s.refcentro}-${idx}`}>
                          <td style={tdStyle}>{s.centro}</td>
                          <td style={tdStyle}>{s.refcentro}</td>
                          <td style={{ ...tdStyle, textAlign: "right" }}>
                            {formatNum(s.quantidade, 2)}
                          </td>
                          <td style={tdStyle}>{s.refunidade}</td>
                          {podeAlterarStock && (
                            <td style={tdStyle}>
                              <MDButton
                                color="info"
                                size="small"
                                variant="outlined"
                                onClick={() => abrirAlterarStock(s)}
                              >
                                Alterar Stock
                              </MDButton>
                            </td>
                          )}
                        </tr>
                      ))}

                      {stocks.length === 0 && (
                        <tr>
                          <td
                            colSpan={podeAlterarStock ? 5 : 4}
                            style={{
                              ...tdStyle,
                              textAlign: "center",
                              color: "#7b809a",
                            }}
                          >
                            Sem stocks registados.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        </>
      )}

      <Dialog open={openStock} onClose={fecharAlterarStock} maxWidth="sm" fullWidth>
        <DialogTitle>Alterar Stock</DialogTitle>

        <DialogContent dividers>
          <MDBox display="grid" gap={1.5}>
            <MDTypography variant="button">
              {material?.refmaterial} — {material?.nome}
            </MDTypography>

            <MDTypography variant="caption" color="text">
              Centro: <b>{stockSelecionado?.centro}</b> ({stockSelecionado?.refcentro})
            </MDTypography>

            <MDBox>
              <div style={labelStyle}>Qtd oficial</div>
              <TextField
                size="small"
                fullWidth
                value={`${formatNum(stockSelecionado?.quantidade, 2)} ${
                  stockSelecionado?.refunidade || ""
                }`}
                disabled
              />
            </MDBox>

            <MDBox>
              <div style={labelStyle}>Qtd verificada</div>
              <TextField
                size="small"
                fullWidth
                value={formStock.qtd_verificada}
                onChange={(e) => setFormStock((p) => ({ ...p, qtd_verificada: e.target.value }))}
              />
            </MDBox>

            <MDBox>
              <div style={labelStyle}>Localização</div>
              <TextField
                size="small"
                fullWidth
                value={formStock.localizacao}
                onChange={(e) =>
                  setFormStock((p) => ({ ...p, localizacao: e.target.value.toUpperCase() }))
                }
              />
            </MDBox>

            <MDBox>
              <div style={labelStyle}>Observações</div>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={formStock.obs}
                onChange={(e) => setFormStock((p) => ({ ...p, obs: e.target.value.toUpperCase() }))}
              />
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={fecharAlterarStock}>
            Cancelar
          </MDButton>
          <MDButton color="info" onClick={gravarAlteracaoStock}>
            Gravar
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid #e0e0e0",
  fontSize: 12,
  color: "#7b809a",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "8px 10px",
  borderBottom: "1px solid #f0f0f0",
  whiteSpace: "nowrap",
};

const labelStyle = {
  fontSize: 12,
  color: "#7b809a",
  marginBottom: 4,
};
